import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle, startViewTransition } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';
import { mountMegaMenuAside } from '@theme/mega-menu-promo-store';

const SUBMENU_ANIMATION_MS = 360;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * Timestamp guard to prevent accidental close during drilldown click transitions.
   * @type {number}
   */
  #drilldownInteractionUntil = 0;

  /**
   * Pin mode: keeps mega menu open for CSS work (?mega-menu-pin=… in URL).
   * @type {boolean}
   */
  #pinMode = false;

  /**
   * @type {((event: KeyboardEvent) => void) | null}
   */
  #escapeListener = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(() => {
      this.#preloadImages();
      mountMegaMenuAside(this);
      this.#initMegaMenuPin();
    });
    window.addEventListener('resize', this.#resizeListener);
    this.addEventListener('click', this.#onLevelTwoClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.removeEventListener('click', this.#onLevelTwoClick);
    this.#setMegaMenuOpen(false);
  }

  /**
   * Toggle mega menu on L1 click (desktop).
   * @param {MouseEvent} event
   */
  toggleMenu = (event) => {
    if (!(event.target instanceof Element)) return;

    if (event.target.closest('.menu-list__submenu, [data-l2-toggle], [data-l2-back]')) return;

    const menuLink = event.target.closest('[ref="menuitem"]');
    if (!(menuLink instanceof HTMLElement)) return;

    if (!menuLink.hasAttribute('aria-haspopup')) return;

    const listItem = menuLink.closest('.menu-list__list-item');
    if (!(listItem instanceof HTMLElement)) return;

    event.preventDefault();
    event.stopPropagation();

    if (menuLink === this.#state.activeItem) {
      this.#resetSubmenuToRoot(listItem);
      return;
    }

    this.activate({ target: listItem, bubbles: true });
  };

  /**
   * Re-clicking the active L1 tab (e.g. SHOP) starts over from the menu's first page.
   * @param {HTMLElement} listItem
   */
  #resetSubmenuToRoot(listItem) {
    const submenu = findSubmenu(listItem);
    if (!submenu) return;

    const containers = /** @type {HTMLElement[]} */ (
      [...submenu.querySelectorAll('[data-menu-list-id]')].filter(
        (container) => container instanceof HTMLElement && container.dataset.drilldown
      )
    );
    if (containers.length === 0) return;

    this.#runDrilldownTransition(() => {
      containers.forEach((container) => {
        this.#resetDrilldown(container);
        this.#updateMegaMenuAside(container, '');
      });
    }, 'back');
  }

  /**
   * Apply a drilldown DOM mutation inside a View Transition so the nav column
   * slides while the promo images cross-dissolve (Figma), instead of snapping.
   * The overlay panel (`mega-menu-panel`, fades) and its nav column
   * (`mega-menu-nav`, slides) are named inline for the duration; mega-menu.css
   * drives the two animations, keyed on the `data-mega-drill` direction. The
   * house helper falls back to a plain call when the API is unavailable,
   * reduced-motion, or low-power.
   * @param {() => void} mutate
   * @param {'forward' | 'back'} [direction] - 'forward' drills into a submenu (nav slides in from the right), 'back' returns (nav slides in from the left).
   */
  #runDrilldownTransition(mutate, direction = 'forward') {
    const root = document.documentElement;
    // Name the two regions inline, scoped to THIS open overlay: a CSS selector
    // would match every rendered header copy at once, and a duplicated
    // `view-transition-name` makes the browser skip the whole transition.
    const panel = this.querySelector('.menu-list__submenu[data-active]');
    const navPane = panel instanceof HTMLElement ? panel.querySelector('.mega-menu__nav-pane') : null;

    // `data-mega-drill` (forward|back) only picks the slide direction (see mega-menu.css).
    root.dataset.megaDrill = direction;
    if (panel instanceof HTMLElement) panel.style.setProperty('view-transition-name', 'mega-menu-panel');
    if (navPane instanceof HTMLElement) navPane.style.setProperty('view-transition-name', 'mega-menu-nav');

    Promise.resolve(startViewTransition(mutate)).finally(() => {
      delete root.dataset.megaDrill;
      if (panel instanceof HTMLElement) panel.style.removeProperty('view-transition-name');
      if (navPane instanceof HTMLElement) navPane.style.removeProperty('view-transition-name');
    });
  }

  /**
   * Close the mega menu overlay.
   */
  closeMenu() {
    if (this.#pinMode) {
      this.unpinMegaMenu();
      return;
    }

    this.#setMegaMenuOpen(false);
    this.#deactivate(undefined, { force: true });
  }

  /**
   * @param {HTMLElement} item
   */
  openMenuItem(item) {
    if (!(item instanceof HTMLElement)) return;
    this.activate({ target: item, bubbles: true });
  }

  /**
   * @param {boolean} open
   */
  #setMegaMenuOpen(open) {
    const header = this.headerComponent;
    if (!header) return;

    if (open) {
      header.setAttribute('data-mega-menu-open', '');
      document.documentElement.setAttribute('scroll-lock', '');

      if (!this.#escapeListener) {
        this.#escapeListener = (event) => {
          if (event.key !== 'Escape' || this.#pinMode) return;
          this.closeMenu();
        };
        document.addEventListener('keydown', this.#escapeListener);
      }
      return;
    }

    const drawerOpen = header.querySelector('#Details-menu-drawer-container[open]');
    if (!drawerOpen) {
      header.removeAttribute('data-mega-menu-open');
      document.documentElement.removeAttribute('scroll-lock');
    }

    if (this.#escapeListener) {
      document.removeEventListener('keydown', this.#escapeListener);
      this.#escapeListener = null;
    }
  }

  /**
   * Exclusive level-2 panel toggling inside each mega-menu list.
   * @param {MouseEvent} event
   */
  #onLevelTwoClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const trigger = event.target.closest('[data-l2-toggle], [data-l2-back]');
    if (!(trigger instanceof HTMLElement)) return;

    this.#drilldownInteractionUntil = Date.now() + 180;

    event.preventDefault();
    event.stopPropagation();

    const container = trigger.closest('[data-menu-list-id]');
    if (!(container instanceof HTMLElement)) return;

    if (trigger.matches('[data-l2-back]')) {
      const parentGroup = trigger.closest('.mega-menu__level2-group');
      this.#runDrilldownTransition(() => {
        this.#resetDrilldown(container);
        this.#updateMegaMenuAside(container, '');

        const focusTarget = parentGroup?.querySelector('[data-l2-toggle]');
        if (focusTarget instanceof HTMLElement) focusTarget.focus();
      }, 'back');
      return;
    }

    const targetId = trigger.getAttribute('data-target');
    if (!targetId) return;

    const panel = container.querySelector(`#${CSS.escape(targetId)}`);
    if (!(panel instanceof HTMLElement)) return;

    this.#runDrilldownTransition(() => {
      this.#resetDrilldown(container);
      container.dataset.drilldown = 'true';
      trigger.setAttribute('aria-expanded', 'true');
      panel.hidden = false;

      const activeGroup = trigger.closest('.mega-menu__level2-group');
      if (activeGroup instanceof HTMLElement) {
        activeGroup.dataset.active = 'true';
        const backButton = activeGroup.querySelector('[data-l2-back]');
        if (backButton instanceof HTMLElement) backButton.hidden = false;
        this.#updateMegaMenuAside(container, activeGroup.dataset.l2Handle || '');
      }
    });
  };

  /**
   * Reset every drilldown container inside a submenu back to its root page.
   * Used when a menu tab is (re)activated so switching tabs and returning
   * never lands on a stale drilldown (e.g. Shop ▸ Saddles) — each tab always
   * opens on its default view.
   * @param {HTMLElement | null | undefined} submenu
   */
  #resetSubmenuDrilldown(submenu) {
    if (!(submenu instanceof HTMLElement)) return;

    submenu.querySelectorAll('[data-menu-list-id]').forEach((container) => {
      if (!(container instanceof HTMLElement)) return;
      this.#resetDrilldown(container);
      this.#updateMegaMenuAside(container, '');
    });
  }

  /**
   * Reset a mega-menu container's L2/L3 drilldown DOM state back to the root list.
   * @param {HTMLElement} container
   */
  #resetDrilldown(container) {
    delete container.dataset.drilldown;
    container.querySelectorAll('[data-l2-toggle]').forEach((item) => item.setAttribute('aria-expanded', 'false'));
    container.querySelectorAll('[data-l2-back]').forEach((item) => {
      if (item instanceof HTMLElement) item.hidden = true;
    });
    container.querySelectorAll('.mega-menu__level2-group').forEach((item) => {
      if (item instanceof HTMLElement) delete item.dataset.active;
    });
    container.querySelectorAll('.mega-menu__level3-list').forEach((item) => {
      if (item instanceof HTMLElement) item.hidden = true;
    });
  }

  /**
   * Swap promo cards when drilling into a level-2 item (e.g. Saddles).
   * @param {HTMLElement} container
   * @param {string} submenuHandle
   */
  #updateMegaMenuAside(container, submenuHandle = '') {
    const grid = container.closest('.mega-menu__grid');
    const chipsPane = grid?.querySelector('.mega-menu__chips-pane');
    const promoPane = grid?.querySelector('.mega-menu__promo-pane');

    if (chipsPane instanceof HTMLElement || promoPane instanceof HTMLElement) {
      if (chipsPane instanceof HTMLElement) {
        chipsPane.dataset.chipsSubmenuHandle = submenuHandle;
        const chipsLazyPane = chipsPane.querySelector('.mega-menu-chips--from-store');
        if (chipsLazyPane instanceof HTMLElement) {
          chipsLazyPane.dataset.chipsSubmenuHandle = submenuHandle;
        }
      }

      if (promoPane instanceof HTMLElement) {
        promoPane.dataset.promoSubmenuHandle = submenuHandle;
        const promoLazyPane = promoPane.querySelector('.mega-menu-promo--from-store');
        if (promoLazyPane instanceof HTMLElement) {
          promoLazyPane.dataset.promoSubmenuHandle = submenuHandle;
        }
      }

      mountMegaMenuAside(grid ?? container);
      return;
    }

    const submenu = container.closest('[ref="submenu[]"]');
    mountMegaMenuAside(submenu ?? container);
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent | { target: Element; bubbles?: boolean }} event
   */
  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    const listItem = event.target.closest('.menu-list__list-item');
    if (!(listItem instanceof HTMLElement)) return;

    const item = findMenuLink(listItem);
    if (!item || item == this.#state.activeItem) return;

    const wasMenuOpen = this.ariaExpanded === 'true';
    const previouslyActiveItem = this.#state.activeItem;

    delete this.dataset.megaMenuAnimateIn;
    delete this.dataset.megaMenuSwitching;

    if (!wasMenuOpen) {
      this.dataset.megaMenuAnimateIn = '';
      setTimeout(() => delete this.dataset.megaMenuAnimateIn, SUBMENU_ANIMATION_MS);
    } else if (previouslyActiveItem) {
      this.dataset.megaMenuSwitching = '';
      requestAnimationFrame(() => {
        requestAnimationFrame(() => delete this.dataset.megaMenuSwitching);
      });
    }

    const isDefaultSlot = listItem.getAttribute('slot') !== 'overflow';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(listItem);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      submenu.classList.remove('menu-list__submenu--closing');
      // Start the activated tab on its root page: discard any drilldown left
      // over from a previous visit so returning to a tab never reopens a stale
      // submenu (e.g. Shop ▸ Saddles).
      this.#resetSubmenuDrilldown(submenu);
      mountMegaMenuAside(submenu);
      submenu.dataset.active = '';
    }

    const overlayHeight = this.#getOverlayHeight();

    this.headerComponent.style.setProperty('--submenu-height', `${overlayHeight}px`);
    this.#setFullOpenHeaderHeight(overlayHeight);
    this.#setMegaMenuOpen(true);
  };

  /**
   * @returns {number}
   */
  #getOverlayHeight() {
    const headerHeight = parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--header-height')
    );
    return Math.max(window.innerHeight - (Number.isNaN(headerHeight) ? 0 : headerHeight), 0);
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   * @param {{ force?: boolean }} [options]
   */
  #deactivate = (item = this.#state.activeItem, { force = false } = {}) => {
    if (this.#pinMode && !force) return;
    if (!item || item != this.#state.activeItem) return;

    const listItem = item.closest('.menu-list__list-item');
    const submenu = listItem ? findSubmenu(listItem) : null;
    this.#resetMegaMenuState(submenu);

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.dataset.overflowExpanded = 'false';
    this.#setMegaMenuOpen(false);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    if (submenu) {
      submenu.classList.add('menu-list__submenu--closing');
      setTimeout(() => {
        submenu.classList.remove('menu-list__submenu--closing');
        if (!this.#state.activeItem) {
          delete submenu.dataset.active;
        }
      }, SUBMENU_ANIMATION_MS);
    }
  };

  /**
   * Reset drilldown and aside state so L2/L3 do not linger after close.
   * @param {HTMLElement | null | undefined} submenu
   */
  #resetMegaMenuState(submenu) {
    if (!(submenu instanceof HTMLElement)) return;

    submenu.querySelectorAll('[data-menu-list-id]').forEach((container) => {
      if (!(container instanceof HTMLElement)) return;

      delete container.dataset.drilldown;

      container.querySelectorAll('[data-l2-toggle]').forEach((toggle) => {
        toggle.setAttribute('aria-expanded', 'false');
      });

      container.querySelectorAll('[data-l2-back]').forEach((backButton) => {
        if (backButton instanceof HTMLElement) backButton.hidden = true;
      });

      container.querySelectorAll('.mega-menu__level2-group').forEach((group) => {
        if (group instanceof HTMLElement) delete group.dataset.active;
      });

      container.querySelectorAll('.mega-menu__level3-list').forEach((panel) => {
        if (panel instanceof HTMLElement) panel.hidden = true;
      });
    });

    submenu.querySelectorAll('.mega-menu__chips-pane').forEach((pane) => {
      if (!(pane instanceof HTMLElement)) return;
      pane.dataset.chipsSubmenuHandle = '';
      const lazyPane = pane.querySelector('.mega-menu-chips--from-store');
      if (lazyPane instanceof HTMLElement) {
        lazyPane.dataset.chipsSubmenuHandle = '';
        lazyPane.replaceChildren();
        delete lazyPane.dataset.chipsMounted;
        delete lazyPane.dataset.chipsActiveSubmenuHandle;
      }
    });

    submenu.querySelectorAll('.mega-menu__promo-pane').forEach((pane) => {
      if (!(pane instanceof HTMLElement)) return;
      pane.dataset.promoSubmenuHandle = '';
      const lazyPane = pane.querySelector('.mega-menu-promo--from-store');
      if (lazyPane instanceof HTMLElement) {
        lazyPane.dataset.promoSubmenuHandle = '';
        lazyPane.replaceChildren();
        delete lazyPane.dataset.promoMounted;
        delete lazyPane.dataset.promoActiveSubmenuHandle;
      }
    });
  }

  /**
   * Calculate and set the full open header height.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  pinMegaMenu(spec) {
    const [menu = '', submenu = ''] = spec.split('/').map((part) => part.trim());
    if (!menu) return;

    this.#pinMode = true;
    this.dataset.megaMenuPinned = '';

    const item = findPinnedMenuItem(this, menu);
    if (!item) {
      console.warn(`[mega-menu-pin] Voce non trovata: "${menu}"`);
      return;
    }

    this.#openPinnedMenuItem(item, submenu);
  }

  unpinMegaMenu() {
    this.#pinMode = false;
    delete this.dataset.megaMenuPinned;
    this.#deactivate(undefined, { force: true });
  }

  #initMegaMenuPin() {
    const spec = parseMegaMenuPinSpec();
    if (!spec) return;

    this.#pinMode = true;
    this.dataset.megaMenuPinned = '';

    const item = findPinnedMenuItem(this, spec.menu);
    if (!item) {
      console.warn(
        `[mega-menu-pin] Voce non trovata: "${spec.menu}". Prova ?mega-menu-pin=shop oppure ?mega-menu-pin=0`
      );
      return;
    }

    requestAnimationFrame(() => {
      this.#openPinnedMenuItem(item, spec.submenu);
    });
  }

  #openPinnedMenuItem(item, submenuHandle) {
    const listItem = item.closest('.menu-list__list-item') ?? item;
    this.activate({ target: listItem, bubbles: true });

    if (!submenuHandle) return;

    requestAnimationFrame(() => {
      const submenu = findSubmenu(listItem);
      const l2Handle = normalizeMenuHandle(submenuHandle);
      const toggle = submenu?.querySelector(
        `.mega-menu__level2-group[data-l2-handle="${l2Handle}"] [data-l2-toggle]`
      );

      if (toggle instanceof HTMLElement) {
        toggle.click();
        return;
      }

      console.warn(`[mega-menu-pin] Voce L2 non trovata: "${submenuHandle}"`);
    });
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

function normalizeMenuHandle(value) {
  return (value || '')
    .toLowerCase()
    .trim()
    .replace(/[^\p{L}\p{N}\s-]/gu, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

function parseMegaMenuPinSpec() {
  const pin = new URLSearchParams(window.location.search).get('mega-menu-pin');
  if (!pin) return null;

  const [menu = '', submenu = ''] = pin.split('/').map((part) => part.trim());
  if (!menu) return null;

  return { menu, submenu };
}

function findPinnedMenuItem(headerMenu, menuSpec) {
  const items = [...headerMenu.querySelectorAll('.menu-list__list-item')].filter(
    (item) => item.getAttribute('slot') !== 'overflow'
  );

  if (/^\d+$/.test(menuSpec)) {
    const link = findMenuLink(items[Number(menuSpec)]);
    return link instanceof HTMLElement ? link : null;
  }

  const normalizedSpec = normalizeMenuHandle(menuSpec);

  const matchedItem = items.find((item) => {
    const title = item.querySelector('.menu-list__link-title')?.textContent;
    if (normalizeMenuHandle(title) === normalizedSpec) return true;

    const href = item.querySelector('.menu-list__link')?.getAttribute('href') || '';
    const hrefHandle = href.split('/').filter(Boolean).pop() || '';
    return normalizeMenuHandle(hrefHandle) === normalizedSpec;
  });

  return matchedItem ? findMenuLink(matchedItem) : null;
}

/**
 * @param {Element} listItem
 * @returns {HTMLElement | null}
 */
function findMenuLink(listItem) {
  if (!(listItem instanceof Element)) return null;

  if (listItem.matches('[ref="menuitem"]')) {
    return /** @type {HTMLElement} */ (listItem);
  }

  return /** @type {HTMLElement | null} */ (listItem.querySelector('[ref="menuitem"]'));
}

/**
 * @param {Element} listItem
 * @returns {HTMLElement | null}
 */
function findSubmenu(listItem) {
  if (!(listItem instanceof Element)) return null;
  const submenu = listItem.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
